<template>
  <div
    id="video-record"
    class="full-height max-width-100"
  >
    <v-layout
      justify-center
      class="select-block mb-2"
    >
      <b-form-select
        box
        aria-label="Record input source"
        class="full-width"
        v-model="recordSource"
        label="Source"
        :disabled="recordedVideo !== null || !browserSupported"
      >
        <option
          v-for="(source, idx) in sources"
          :key="idx"
          :value="source.value"
        >
          {{source.text}}
        </option>
      </b-form-select>
    </v-layout>
    <div
      class="video-block flex-all-center max-width-100"
      :class="{'recording-error': recordingError}"
    >
      <div
        class="relative text-xs-center"
        v-if="!recordingError && cardTab === 0"
      >
        <video
          class="max-width-100"
          :muted="recordedVideo === null"
          :controls="recordedVideo !== null"
          autoplay
          allowfullscreen
          playsinline
          ref="videoPreview"
        ></video>
      </div>
      <v-layout
        v-else
        class="full-height"
        justify-center
        align-center
        column
      >
        <v-icon
          class="mb-4"
          color="white"
          size="100"
        >{{recordingError.icon}}</v-icon>
        <div
          role="alert"
          v-html="recordingError.message"
        ></div>
      </v-layout>
    </div>
    <v-layout
      v-show="recordSource !== 'screen'"
      grid-list-xs
      align-center
      justify-space-between
      class="volume-block"
    >
      <v-flex
        class="volume-mark-block"
        v-for="(mark, idx) in volumeMarks"
        :key="idx"
      >
        <div
          class="volume-mark"
          :class="{'active': mark}"
        ></div>
      </v-flex>
    </v-layout>
    <v-layout
      class="action-block"
      :class="{'is-recording': isRecording}"
      align-center
      justify-center
    >
      <transition name="fade-left">
        <v-btn
          aria-label="Delete recorded video"
          v-if="recordedVideo !== null"
          color="error"
          outline
          fab
          small
          @click="deleteRecordedVideo"
          :disabled="loading.enabled() || recordingError !== false"
        >
          <v-icon class="d-inline-flex">delete</v-icon>
        </v-btn>
      </transition>
      <v-btn
        :aria-label="recorder === null ? 'Start recording' : 'Stop recording'"
        class="record-stop-button"
        :color="recorder === null ? 'primary' : 'error'"
        outline
        fab
        @click="recorder === null ? startRecording() : stopRecording()"
        :disabled="recordedVideo !== null || recordingError !== false"
      >
        <v-icon class="d-inline-flex">{{recorder === null ? 'fiber_manual_record' : 'stop'}}</v-icon>
      </v-btn>
      <transition name="fade-right">
        <v-btn
          aria-label="Upload recorded video"
          v-if="recordedVideo !== null"
          class="upload-button"
          :class="{'pulse-button': recordedVideo !== null && !loading.enable && recordingError === false}"
          color="success"
          outline
          fab
          small
          @click="uploadRecordedVideo"
          :disabled="loading.enabled() || recordingError !== false"
        >
          <v-icon class="d-inline-flex">file_upload</v-icon>
          <div class="pulse-circle pulse-success"></div>
        </v-btn>
      </transition>
    </v-layout>
    <v-layout
      class="time-block"
      align-center
      justify-center
    >
      <transition name="fade-time">
        <div
          v-if="isRecording"
          class="headline font-weight-thin"
        >
          {{recordingDuration}}
        </div>
      </transition>
    </v-layout>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { BFormSelect } from 'bootstrap-vue';
import VideoTranscoderService from '../services/VideoTranscoderService';
import EventBus from '@snippets/js/eventBus';
import axios from 'axios';
import RecordRTC from 'recordrtc/RecordRTC.js';
import FakeProgress from '../js/fake-progress.js';
import {
  lightenDarkenHex,
} from '@utilities';
const CancelToken = axios.CancelToken;

export default {
  name: 'video-record',
  components: {
    BFormSelect,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
    teacherId: {
      type: Number,
      required: true,
    },
    startingKey: {
      type: String,
      validator: sk => ['webcam', 'screen', 'upload'].includes(sk),
    },
    studentId: {
      type: Number,
      default: null
    },
    bulbAttemptId: {
      type: Number,
      default: null
    },
    questionId: {
      type: Number,
      default: null
    },
    discussionId: {
      type: Number,
      default: null
    },
    bulbId: {
      type: Number,
      default: null
    },
    lessonId: {
      type: Number,
      default: null
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '100%'
    },
    maxSize: {
      type: String,
      default: '1000MB'
    },
    maxDuration: {
      type: Number,
      default: 3600
    },
    mobile: {
      type: Boolean,
      default: false
    },
    showCloseButton: {
      type: [String, Boolean],
      default: true,
      validator: scb => [true, false, 'left'].includes(scb)
    },
    debug: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    ...mapState('videoTranscoder', [
      'generateRandomNumber',
    ]),
    stream: null,
    recorder: null,
    camera: null,
    existingSrc: null,
    isRecording: false,
    recordedVideo: null,
    recordingTime: {
      current: 0,
      start: 0
    },
    recordingError: false,
    markBar: 0,
    source: null,
    sources: [
    {
      text: 'Webcam',
      value: 'webcam'
    },
    {
      text: 'Screen',
      value: 'screen'
    },
    ],
    volumeMarks: [false, false, false, false, false, false, false, false, false, false],
    canceled: false,
    uploadProgress: 0,
    fakeUploadProgress: null,
    uploadProgressInterval: null,
    assetProgress: 0,
    fakeAssetProgress: null,
    assetProgressInterval: null,
    browserSupported: true,
    recordSource: 'webcam'
	}),
	computed: {
		...mapState('videoTranscoder', [
      'loading',
      'userAgent',
      'keyboardFocus',
      'transcoderEnvironment',
      'queryString',
      'cardTab'
    ]),
    recordingDuration() {
      if (this.recordingTime.start === 0 || this.recordingTime.current === 0) {
        return new Date(0).toISOString().substr(14, 5);
      }
      return new Date(Math.round(((this.recordingTime.current - this.recordingTime.start)) / 1000) * 1000).toISOString().substr(14, 5);
    },
	},
	methods: {
    ...mapMutations('videoTranscoder', [
      'toggleLoading',
      'updateLoadingByIndex',
    ]),
    startUploadProgress(bytes) {
      let percentage = (bytes / 104857600);
      let percentageSeconds = (percentage * 200) * 100;
      this.fakeUploadProgress = new FakeProgress({
        timeConstant: percentageSeconds,
        autoStart: true
      });
      this.initUploadRangeInterval();
    },
    stopUploadProgress() {
      if (this.fakeUploadProgress) this.fakeUploadProgress.end();
      clearInterval(this.uploadProgressInterval);
    },
    initUploadRangeInterval(min = 1000, max = 3000) {
      clearInterval(this.uploadProgressInterval);
      let random = this.generateRandomNumber(min, max);
      this.uploadProgressInterval = setTimeout(() => {
        this.updateLoadingByIndex([
          {
            index: 1,
            progress: parseFloat((this.fakeUploadProgress.progress * 100).toFixed(1))
          }
        ])
        this.initUploadRangeInterval(min, max);
      }, random);
    },
    startAssetProgress(bytes) {
      let percentage = (bytes / 104857600);
      let percentageSeconds = (percentage * 350) * 100;
      this.fakeAssetProgress = new FakeProgress({
        timeConstant: percentageSeconds,
        autoStart: true
      });
      this.initAssetRangeInterval();
    },
    stopAssetProgress() {
      if (this.fakeAssetProgress) this.fakeAssetProgress.end();
      clearInterval(this.assetProgressInterval);
    },
    initAssetRangeInterval(min = 1000, max = 3000) {
      clearInterval(this.assetProgressInterval);
      let random = this.generateRandomNumber(min, max);
      this.assetProgressInterval = setTimeout(() => {
        this.updateLoadingByIndex([
          {
            index: 2,
            progress: parseFloat((this.fakeAssetProgress.progress * 100).toFixed(1))
          }
        ])
        this.initAssetRangeInterval(min, max);
      }, random);
    },
    captureSource(bypassExistingSource) {
      if (!bypassExistingSource && this.camera && this.camera.sourceType === this.recordSource) {
        return new Promise((resolve, reject) => {
          resolve(this.camera);
        });
      }
      return this.recordSource === 'screen' ? this.captureScreen() : this.captureWebcam();
    },
    captureWebcam() {
      this.recordingError = false;
      return new Promise((resolve, reject) => {
        this.recordingTime.start = 0;
        this.recordingTime.current = 0;
        return navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: 9999,
            height: 9999
          },
        })
        .then(async (media) => {
          /*
            We need to do this part in a try/catch block because if the user doesn't have a microphone (or hasn't granted permission for that microphone to be used) then it'll throw an error.
          */
          try {
            const audio = await navigator.mediaDevices.getUserMedia({
              audio: true,
            });
            if (audio) media.addTrack(audio.getTracks()[0]);
          } catch(e) {
            
          }
          return resolve(media);
        })
        .catch(reject);
      });
    },
    async captureScreen() {
      this.recordingError = false;
      this.recordingTime.start = 0;
      this.recordingTime.current = 0;
      let media;
      if (navigator.mediaDevices.getDisplayMedia) {
        media = await navigator.mediaDevices.getDisplayMedia({
          video: {
            mandatory: {
              maxWidth: screen.width,
              maxHeight: screen.height,
            }
          },
        });
      } else if (navigator.getDisplayMedia) {
        media = await navigator.getDisplayMedia({
          video: {
            mandatory: {
              maxWidth: screen.width,
              maxHeight: screen.height,
            }
          },
        });
      } else {
        throw new Error('Your browser does not support screen recording at this time.');
      }
      /*
        We need to do this part in a try/catch block because if the user doesn't have a microphone (or hasn't granted permission for that microphone to be used) then it'll throw an error.
      */
      try {
        const audio = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        if (audio) media.addTrack(audio.getTracks()[0]);
      } catch(e) {

      }
      return media;
    },
    initRecording(bypassExistingSource) {
        if (!this.browserSupported) return;
      this.captureSource(bypassExistingSource)
      .then(source => {
        source.sourceType = this.recordSource;
        if (this.recordSource === 'screen') this.handleScreenSource(source);
        else this.handleWebcamSource(source);
        this.$nextTick(() => {
          let button = document.querySelector('button.record-stop-button');
          if (button && this.keyboardFocus) button.focus();
        });
      })
      .catch(this.handleRecordingError);
    },
    handleScreenSource(screen) {
      this.camera = screen;
      if (this.$refs.videoPreview) {
        this.$refs.videoPreview.srcObject = screen;
      } else {
        this.stopRecording();
      }
    },
    handleWebcamSource(webcam) {
      if (webcam.getAudioTracks().length) {
        /*
          We only want to do this stuff with AudioContext if the user both has a microphone and has given permission to use said microphone.
        */
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(webcam);
        const javascriptNode = audioContext.createScriptProcessor(2048, 1, 1);

        analyser.smoothingTimeConstant = 0.8;
        analyser.fftSize = 1024;

        microphone.connect(analyser);
        analyser.connect(javascriptNode);
        javascriptNode.connect(audioContext.destination);
        javascriptNode.onaudioprocess = () => {
          if (this.cardTab !== 0 || this.recordSource === 'screen') return;
          var array = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(array);
          var values = 0;
          var length = array.length;
          for (var i = 0; i < length; i++) {
            values += (array[i]);
          }
          let markBar = Math.floor((values / length) / 10);
          this.setVolumeMarks(markBar > 10 ? 10 : markBar - 1);
        }
      }
      this.camera = webcam;
      if (this.$refs.videoPreview) this.$refs.videoPreview.srcObject = webcam;
      else this.stopRecording();
    },
    setVolumeMarks: _.throttle(function(bar) {
      this.volumeMarks = this.volumeMarks.map((vm, idx) => idx <= bar && bar !== 0);
    }, 100, {
      leading: true
    }),
    handleRecordingError(error) {
      this.stopRecording();
      const chromeRecommendation = (_.has(this.userAgent, 'browser.name') && this.userAgent.browser.name !== 'Chrome') ? 'Try using <a href="https://www.google.com/chrome/" class="error-link white--text" target="_blank">Google Chrome</a> for a more feature-rich PlayPosit experience. ' : '';
      if (this.debug && typeof error !== 'string') console.log(error.name, error.type, error.msg || error.message)
      else if (this.debug) console.log(error);
      if (typeof error === 'string') {
        if (error === 'Your browser does not supports Media Recorder API. Please try other modules e.g. WhammyRecorder or StereoAudioRecorder.') {
          this.recordingError = {
            message: `<span class="subheading white--text">Your browser does not support media recording at this time. ${chromeRecommendation}</span>`.trim(),
            icon: 'not_interested'
          };
        } else {
          this.recordingError = {
            message: `<span class="subheading white--text">${error} ${chromeRecommendation}</span>`.trim(),
            icon: 'error_outline'
          };
        }
      } else if (error.name === 'NotFoundError') {
        this.recordingError = {
          message: '<span class="subheading white--text">Unable to detect a webcam.</span>',
          icon: 'videocam_off'
        };
      } else if (error.name === 'NotAllowedError') {
        this.recordingError = {
          message: `<span class="subheading white--text">Permission to record ${this.recordSource === 'screen' ? 'your screen' : 'audio/video'} was denied.</span> <br/><br/><a href="https://knowledge.playposit.com/article/189-audio-recording" class="error-link white--text underline" target="_blank">Click here to learn how to grant permission in your browser.</a>`,
          icon: 'not_interested'
        };
      } else {
        this.recordingError = {
          message: `<span class="subheading white--text">Unable to to record ${this.recordSource === 'screen' ? 'your screen' : 'audio/video'} at this time. ${chromeRecommendation}If you need more help, please contact PlayPosit support at <a class="error-link white--text" href="mailto:help@playposit.org">help@playposit.org</a>.</span>`.trim(),
          icon: 'error_outline'
        };
      }
      this.recorder = null;
      this.camera = null;
      this.$nextTick(() => {
        let errorLink = document.querySelector('a.error-link');
        if (errorLink && this.keyboardFocus) errorLink.focus();
      });
    },
    startRecording() {
      this.captureSource()
      .then(camera => {
        this.$refs.videoPreview.muted = true;
        this.$refs.videoPreview.volume = 0;
        this.$refs.videoPreview.srcObject = camera;
        this.recorder = RecordRTC(camera, {
          type: 'video',
          disableLogs: true
        });
        this.recorder.startRecording();
        this.isRecording = true;
        this.recorder.camera = camera;
        this.recordingTime.start = Date.now();
      })
      .catch(this.handleRecordingError);
    },
    stopRecording() {
      if (this.recorder) {
        this.recorder.stopRecording(() => {
          this.isRecording = false;
          this.recordedVideo = this.recorder.getBlob();
          if (this.$refs.videoPreview) {
            this.$refs.videoPreview.src = this.$refs.videoPreview.srcObject = null;
            this.$refs.videoPreview.muted = false;
            this.$refs.videoPreview.volume = 1;
            this.$refs.videoPreview.src = URL.createObjectURL(this.recordedVideo);
            this.existingSrc = URL.createObjectURL(this.recordedVideo);
          } else {
            this.existingSrc = URL.createObjectURL(this.recordedVideo);
          }
          this.recorder.camera.stop();
          this.recorder.camera.getTracks().forEach(t => t.stop());
          this.recorder.destroy();
          this.recorder = null;
        });
      } else if (this.camera) {
        this.camera.stop();
        this.camera.getTracks().forEach(t => t.stop());
      }
    },
    toggleRecordingTimeInterval() {
      if (!this.recordingTimeInterval) {
        this.recordingTimeInterval = setInterval(() => {
          this.recordingTime.current = Date.now();
        }, 1000);
      } else {
        clearInterval(this.recordingTimeInterval);
        this.recordingTimeInterval = false;
      }
    },
    deleteRecordedVideo() {
      this.recordedVideo = null;
      this.existingSrc = null;
      this.initRecording(true);
    },
    pollMuxUpload(pollData, validator) {
      if (this.canceled) {
        clearTimeout(this.uploadProgressTimeout);
        return clearInterval(this.uploadInterval);
      }
      this.updateLoadingByIndex([
        {
          index: 0,
          progress: 100,
          status: 'success'
        },
        {
          index: 1,
          status: true
        }
      ]);
      return new Promise((resolve, reject) => {
        return VideoTranscoderService.pollMuxUpload(pollData)
          .then(({data}) => {
            if (validator(data)) {
              return resolve(data);
            } else {
              this.uploadInterval = setInterval(() => {
                if (this.canceled) {
                  clearTimeout(this.uploadProgressTimeout);
                  return clearInterval(this.uploadInterval);
                }
                return VideoTranscoderService.pollMuxUpload(pollData)
                .then(({data}) => {
                  if (validator(data)) {
                    clearInterval(this.uploadInterval);
                    clearTimeout(this.uploadProgressTimeout);
                    this.updateLoadingByIndex({
                      index: 1,
                      progress: 100
                    });
                    setTimeout(() => {
                      return resolve(data);
                    }, 500);
                  }
                })
                .catch(error => {
                  clearInterval(this.uploadInterval);
                  clearTimeout(this.uploadProgressTimeout);
                  return reject(error);
                });
              }, 5000);
            }
          })
          .catch(error => {
            clearInterval(this.uploadInterval);
            clearTimeout(this.uploadProgressTimeout);
            reject(error);
          });
      });
    },
    pollMuxAsset(pollData, validator) {
      if (this.canceled) {
        clearTimeout(this.assetProgressTimeout);
        return clearInterval(this.assetInterval);
      }
      this.updateLoadingByIndex([
        {
          index: 1,
          progress: 100,
          status: 'success'
        },
        {
          index: 2,
          status: true
        }
      ]);
      return new Promise((resolve, reject) => {
        return VideoTranscoderService.pollMuxAsset(pollData)
          .then(({data}) => {
            if (validator(data)) {
              return resolve(data);
            } else {
              this.assetInterval = setInterval(() => {
                if (this.canceled) {
                  clearTimeout(this.assetProgressTimeout);
                  return clearInterval(this.assetInterval);
                }
                return VideoTranscoderService.pollMuxAsset(pollData)
                .then(({data}) => {
                  if (validator(data)) {
                    clearInterval(this.assetInterval);
                    clearTimeout(this.assetProgressTimeout);
                    this.updateLoadingByIndex({
                      index: 2,
                      progress: 100
                    });
                    setTimeout(() => {
                      return resolve(data);
                    }, 500);
                  }
                })
                .catch(error => {
                  clearInterval(this.assetInterval);
                    clearTimeout(this.assetProgressTimeout);
                  reject(error);
                });
              }, 10000);
            }
          })
          .catch(error => {
            clearInterval(this.assetInterval);
            clearTimeout(this.assetProgressTimeout);
            reject(error);
          });
      });
    },
    uploadRecordedVideo() {
      this.$refs.videoPreview.pause();
      this.initUploadProcess();
      var formData = new FormData();
      formData.append('file', this.recordedVideo);
      formData.append('playerDuration', this.$refs.videoPreview.duration);
      const cancel = () => {
        return new Promise((resolve, reject) => {
          this.source.cancel();
          this.stopUploadProgress();
          this.stopAssetProgress();
          clearInterval(this.uploadInterval);
          clearInterval(this.assetInterval);
          return resolve();
        });
      }
      this.updateLoadingByIndex([
        {
          index: 0,
          cancel
        },
        {
          index: 1,
          cancel
        },
        {
          index: 2,
          cancel
        },
      ]);
      if (this.canceled) return cancel();
      VideoTranscoderService.transcodeVideo({
        formData,
        cancelToken: this.source.token,
        teacherId: this.teacherId,
        studentId: this.studentId,
        queryString: this.queryString,
        onUploadProgress: event => {
          let obj = {
            index: 0,
            progress: parseFloat(((event.loaded / event.total) * 100).toFixed(1)),
          }
          if (event.loaded === event.total) obj.status = 'success';
          this.updateLoadingByIndex(obj);
        }
      })
      .then(({data: upload}) => {
        if (this.canceled) return cancel();
        this.startUploadProgress(this.recordedVideo.size);
        return this.pollMuxUpload({
          muxUploadId: upload.id,
          cancelToken: this.source.token,
          teacherId: this.teacherId
        }, upload => {
          return _.has(upload, 'status') && upload.status === 'asset_created';
        });
      })
      .then(upload => {
        this.stopUploadProgress();
        if (this.canceled) return cancel();
        this.startAssetProgress(this.recordedVideo.size);
        return this.pollMuxAsset({
          muxAssetId: upload.assetId,
          cancelToken: this.source.token,
          teacherId: this.teacherId
        }, video => {
          return _.has(video, 'status') && video.status === 'ready';
        });
      })
      .then(muxVideo => {
        this.stopAssetProgress();
        this.updateLoadingByIndex({
          index: 2,
          progress: 100,
          status: 'success'
        });
        muxVideo.name = `${Date.now()}`;
        muxVideo.playposit = true;
        muxVideo.success = true;
        this.$emit('success', muxVideo);
        this.stopRecording();
      })
      .catch(error => {
        if (axios.isCancel(error)) return;
        this.updateLoadingByIndex({
          index: this.loading.activeIndex(),
          status: 'error',
          error: error.msg || error.message || 'Unable to upload video at this time.'
        });
        this.stopUploadProgress();
        this.stopAssetProgress();
        clearInterval(this.uploadInterval);
        clearInterval(this.assetInterval);
        this.$emit('error', error);
      });
    },
    initUploadProcess() {
      this.canceled = false;
      this.source = CancelToken.source();
      this.toggleLoading([
        {
          title: 'Uploading',
          success: 'Uploaded',
          color: lightenDarkenHex(this.$vuetify.theme.primary, 0.2, 20)
        },
        {
          title: 'Processing',
          success: 'Processed',
          color: this.$vuetify.theme.primary
        },
        {
          title: 'Transcoding',
          success: 'Transcoded',
          color: lightenDarkenHex(this.$vuetify.theme.primary, -0.2)
        }
      ]);
    },
    initEvents() {
      EventBus.$on('verify-exit-permissions', cb => {
        if (this.cardTab !== 0) return;
        if (this.loading.enabled()) {
          cb(false);
        } else if (this.isRecording) {
          cb(false, 'You are currently recording a video. Exiting this modal will cancel this process.');
        } else if (this.recordedVideo !== null) {
          cb(false, 'Exiting this modal will delete your recorded video.')
        } else {
          cb(true);
        }
      });
      EventBus.$on('cancel-transcode', () => {
        this.canceled = true;
      });
    }
  },
  watch: {
    isRecording() {
      this.toggleRecordingTimeInterval();
    },
    recordSource(n, o) {
      if (this.camera) {
        this.camera.stop();
        this.camera.getTracks().forEach(t => t.stop());
      }
      if (o !== null) this.initRecording();
    },
    cardTab(n, o) {
      if (n) {
        this.stopRecording();
      }
      else if (this.existingSrc) {
        this.$nextTick(() => {
          if (this.$refs.videoPreview) {
            this.$refs.videoPreview.src = this.existingSrc;
          } else {
            this.initRecording(true);
          }
        });
      } else {
        if (this.recordedVideo === null) this.initRecording(true);
      }
    }
  },
  beforeDestroy() {
    this.stopRecording();
    if (this.source) this.source.cancel();
  },
  mounted() {
    if (this.startingKey === 'screen') this.recordSource = 'screen';
    if (this.cardTab === 0) this.initRecording();
    this.initEvents();
    if (
      _.get(this, 'userAgent.browser.name') &&
      (
        !this.userAgent.browser.name.toLowerCase().includes('chrome') &&
        !this.userAgent.browser.name.toLowerCase().includes('edge')
      )
    ) {
        this.browserSupported = false;
        this.recordingError = {
          message: `<span class="subheading white--text">Unable to support webcam/screen recording in this browser.</span> <br/><br/><span class="subheading white--text">Try using <a href="https://www.google.com/chrome/" class="error-link white--text" target="_blank">Google Chrome</a> for a more feature-rich PlayPosit experience.</span>`,
          icon: 'not_interested'
        };
    }
  }
};
</script>


<style lang="scss">
#video-record {
  .custom-select {
    display: inline-block;
    width: 100%;
    height: calc(1.5em + .75rem + 2px);
    padding: .375rem 1.75rem .375rem .75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #495057;
    vertical-align: middle;
    background: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23343a40' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center/8px 10px;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: .25rem;
    appearance: none;

    &:focus {
      border-color: currentColor !important;
      box-shadow: none !important;
    }
    &[box] {
      border: 0;
      background-color: #f5f5f5;
      &:not([small]) {
        height: 56px !important;
      }
    }

    .max-width-100 {
      max-width: 100%;
    }
  }

  .video-block {
    &.recording-error {
      background-color: #FF5252;
      padding: 24px;
      text-align: center;
    }
  }
  .volume-block {
    height: 25px;
    .volume-mark-block {
      &:first-of-type {
        padding-right: 2px;
      }
      &:last-of-type {
        padding-left: 2px;
      }
      &:not(:last-of-type):not(:first-of-type) {
        padding: 0 2px;
      }
      .volume-mark {
        height: 10px;
        border: 1px solid var(--branding-active-hex);
        transition: background-color 0.1s linear;
        &.active {
          background-color: var(--branding-active-hex);
        }
      }
    }
  }
  .action-block {
    position: relative;
    transform: translateY(20px);
    transition: transform .150s linear;

    &.is-recording {
      transform: translateY(0);
    }

    .record-stop-button {
      z-index: 2;
      background-color: white !important;
      &.v-btn--disabled {
        background-color: #E0E0E0 !important;
      }
    }

    .upload-button {
      .v-btn__content {
        height: 38px;
        width: 38px;
        i {
          position: static !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
        }
      }
    }

    .pulse-circle {
      position: absolute;
      border-radius: 50%;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      opacity: 1 !important;
      transform: none;
    }
  }

  .time-block {
    min-height: 32px;
  }

  .d-inline-flex {
    display: inline-flex !important;
  }
}
</style>
